import { PropsWithChildren } from "react";

import { css, cssMap } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { ExportableAdgColorTypes } from "./adg-colors";
import { getGradient, ValidAngles } from "./gradients";

const positioner = {
    cornerTopLeft: "top left",
    cornerTopRight: "top right",
    cornerBottomLeft: "bottom left",
    cornerBottomRight: "bottom right",
    sideTop: "center top",
    sideLeft: "left center",
    sideRight: "right center",
    sideBottom: "center bottom",
    hidden: "-100% -100%"
};

const sizes = {
    desktop: {
        cornerTopLeft: "400px auto",
        cornerTopRight: "400px auto",
        cornerBottomLeft: "400px auto",
        cornerBottomRight: "400px auto",
        sideTop: "auto 150px",
        sideLeft: "400px auto",
        sideRight: "400px auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    tablet: {
        cornerTopLeft: "auto 50%",
        cornerTopRight: "auto 50%",
        cornerBottomLeft: "auto 50%",
        cornerBottomRight: "auto 50%",
        sideTop: "auto 150px",
        sideLeft: "calc(50% - 350px) auto",
        sideRight: "calc(50% - 350px) auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    mobile: {
        cornerTopLeft: "auto 200px",
        cornerTopRight: "auto 200px",
        cornerBottomLeft: "auto 200px",
        cornerBottomRight: "auto 200px",
        sideTop: "auto 150px",
        sideLeft: "50%",
        sideRight: "50%",
        sideBottom: "auto 200px",
        hidden: "0 0"
    }
};

export type ValidPositions =
    | "cornerTopLeft"
    | "cornerTopRight"
    | "cornerBottomLeft"
    | "cornerBottomRight"
    | "sideTop"
    | "sideLeft"
    | "sideRight"
    | "sideBottom"
    | "hidden";

export interface PositionProperties {
    imageLink: string;
    positionDesktop: ValidPositions;
    positionTablet: ValidPositions;
    positionMobile: ValidPositions;
}

export interface BGProps {
    isCompiled?: boolean; // not used in this file but kept while we still have BGStyles
    bgColor?: ExportableAdgColorTypes;
    textColor?: ExportableAdgColorTypes;
    gradientDirection?: ValidAngles;
    bgColor2?: ExportableAdgColorTypes;
    bgImage?: string;
    partialBackgrounds?: PositionProperties[];
}

const getBGObject = (bg: PositionProperties) => {
    return {
        src: `url(${bg.imageLink})`,
        posDesktop: positioner[bg.positionDesktop],
        posTablet: positioner[bg.positionTablet],
        posMobile: positioner[bg.positionMobile],
        sizeDesktop: sizes.desktop[bg.positionDesktop],
        sizeTablet: sizes.tablet[bg.positionTablet],
        sizeMobile: sizes.mobile[bg.positionMobile]
    };
};

const backgroundColorMap = cssMap({
    n0: { backgroundColor: "#ffffff" },
    n10: { backgroundColor: "#fafbfc" },
    n20: { backgroundColor: "#f4f5f7" },
    n30: { backgroundColor: "#ebecf0" },
    n40: { backgroundColor: "#dfe1e5" },
    n50: { backgroundColor: "#c1c7d0" },
    n60: { backgroundColor: "#b3bac5" },
    n70: { backgroundColor: "#a5adba" },
    n80: { backgroundColor: "#97a0af" },
    n90: { backgroundColor: "#8993a4" },
    n100: { backgroundColor: "#7a869a" },
    n200: { backgroundColor: "#6c798f" },
    n300: { backgroundColor: "#5e6c84" },
    n400: { backgroundColor: "#505f79" },
    n500: { backgroundColor: "#42526e" },
    n600: { backgroundColor: "#344563" },
    n700: { backgroundColor: "#253858" },
    n800: { backgroundColor: "#172b4d" },
    n900: { backgroundColor: "#091e42" },
    y500: { backgroundColor: "#ff8b00" },
    y400: { backgroundColor: "#ff991f" },
    y300: { backgroundColor: "#ffab00" },
    y200: { backgroundColor: "#ffc400" },
    y100: { backgroundColor: "#ffe380" },
    y75: { backgroundColor: "#fff0b2" },
    y50: { backgroundColor: "#fffae5" },
    g900: { backgroundColor: "#164B35" },
    g500: { backgroundColor: "#006644" },
    g400: { backgroundColor: "#00875a" },
    g300: { backgroundColor: "#36b37e" },
    g200: { backgroundColor: "#57d9a3" },
    g100: { backgroundColor: "#79f2c0" },
    g75: { backgroundColor: "#abf5d1" },
    g50: { backgroundColor: "#e3fcef" },
    t500: { backgroundColor: "#008da6" },
    t400: { backgroundColor: "#00a3bf" },
    t300: { backgroundColor: "#00b8d9" },
    t200: { backgroundColor: "#00c7e5" },
    t100: { backgroundColor: "#79e2f2" },
    t75: { backgroundColor: "#b3f5ff" },
    t50: { backgroundColor: "#e6fcff" },
    b500: { backgroundColor: "#0049b0" },
    b400: { backgroundColor: "#0052cc" },
    b300: { backgroundColor: "#0065ff" },
    b200: { backgroundColor: "#2684ff" },
    b100: { backgroundColor: "#4c9aff" },
    b75: { backgroundColor: "#b2d4ff" },
    b50: { backgroundColor: "#deebff" },
    r500: { backgroundColor: "#bf2600" },
    r400: { backgroundColor: "#de350b" },
    r300: { backgroundColor: "#ff5630" },
    r200: { backgroundColor: "#ff7452" },
    r100: { backgroundColor: "#ff8f73" },
    r75: { backgroundColor: "#ffbdad" },
    r50: { backgroundColor: "#ffebe5" },
    p500: { backgroundColor: "#403294" },
    p400: { backgroundColor: "#5243aa" },
    p300: { backgroundColor: "#6554c0" },
    p200: { backgroundColor: "#8777d9" },
    p100: { backgroundColor: "#998dd9" },
    p75: { backgroundColor: "#c0b6f2" },
    p50: { backgroundColor: "#eae6ff" }
});

const textColorMap = cssMap({
    n0: { color: "#ffffff" },
    n10: { color: "#fafbfc" },
    n20: { color: "#f4f5f7" },
    n30: { color: "#ebecf0" },
    n40: { color: "#dfe1e5" },
    n50: { color: "#c1c7d0" },
    n60: { color: "#b3bac5" },
    n70: { color: "#a5adba" },
    n80: { color: "#97a0af" },
    n90: { color: "#8993a4" },
    n100: { color: "#7a869a" },
    n200: { color: "#6c798f" },
    n300: { color: "#5e6c84" },
    n400: { color: "#505f79" },
    n500: { color: "#42526e" },
    n600: { color: "#344563" },
    n700: { color: "#253858" },
    n800: { color: "#172b4d" },
    n900: { color: "#091e42" },
    y500: { color: "#ff8b00" },
    y400: { color: "#ff991f" },
    y300: { color: "#ffab00" },
    y200: { color: "#ffc400" },
    y100: { color: "#ffe380" },
    y75: { color: "#fff0b2" },
    y50: { color: "#fffae5" },
    g900: { color: "#164B35" },
    g500: { color: "#006644" },
    g400: { color: "#00875a" },
    g300: { color: "#36b37e" },
    g200: { color: "#57d9a3" },
    g100: { color: "#79f2c0" },
    g75: { color: "#abf5d1" },
    g50: { color: "#e3fcef" },
    t500: { color: "#008da6" },
    t400: { color: "#00a3bf" },
    t300: { color: "#00b8d9" },
    t200: { color: "#00c7e5" },
    t100: { color: "#79e2f2" },
    t75: { color: "#b3f5ff" },
    t50: { color: "#e6fcff" },
    b500: { color: "#0049b0" },
    b400: { color: "#0052cc" },
    b300: { color: "#0065ff" },
    b200: { color: "#2684ff" },
    b100: { color: "#4c9aff" },
    b75: { color: "#b2d4ff" },
    b50: { color: "#deebff" },
    r500: { color: "#bf2600" },
    r400: { color: "#de350b" },
    r300: { color: "#ff5630" },
    r200: { color: "#ff7452" },
    r100: { color: "#ff8f73" },
    r75: { color: "#ffbdad" },
    r50: { color: "#ffebe5" },
    p500: { color: "#403294" },
    p400: { color: "#5243aa" },
    p300: { color: "#6554c0" },
    p200: { color: "#8777d9" },
    p100: { color: "#998dd9" },
    p75: { color: "#c0b6f2" },
    p50: { color: "#eae6ff" }
});

// eslint-disable-next-line complexity
export const StyledBackground = (
    backgroundProperties: PropsWithChildren<BGProps>
) => {
    const obj = backgroundProperties || {};

    const hasGradient = obj.gradientDirection && obj.bgColor && obj.bgColor2;

    const totalBGs = [];
    const collections = {
        desktop: {
            topCorners: false,
            bottomCorners: false
        },
        tablet: {
            topCorners: false,
            bottomCorners: false
        },
        mobile: {
            topCorners: false,
            bottomCorners: false
        }
    };

    if (hasGradient) {
        totalBGs.unshift({
            src: getGradient(obj.gradientDirection, obj.bgColor, obj.bgColor2),
            posDesktop: "top left",
            posTablet: "top left",
            posMobile: "top left",
            sizeDesktop: "100% 100%",
            sizeTablet: "100% 100%",
            sizeMobile: "100% 100%"
        });
    }

    if (obj.bgImage) {
        totalBGs.unshift({
            src: `url(${obj.bgImage} )`,
            posDesktop: "center center",
            posTablet: "center center",
            posMobile: "center center",
            sizeDesktop: "cover",
            sizeTablet: "cover",
            sizeMobile: "cover"
        });
    }

    if (obj.partialBackgrounds) {
        obj.partialBackgrounds.forEach((bg) => {
            totalBGs.unshift(getBGObject(bg));
            if (
                bg.positionDesktop.startsWith("cornerTop") ||
                bg.positionDesktop == "sideTop"
            ) {
                collections.desktop.topCorners = true;
            }
            if (
                bg.positionDesktop.startsWith("cornerBottom") ||
                bg.positionDesktop == "sideBottom"
            ) {
                collections.desktop.bottomCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerTop") ||
                bg.positionTablet == "sideTop"
            ) {
                collections.tablet.topCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerBottom") ||
                bg.positionTablet == "sideBottom"
            ) {
                collections.tablet.bottomCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerTop") ||
                bg.positionMobile == "sideTop"
            ) {
                collections.mobile.topCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerBottom") ||
                bg.positionMobile == "sideBottom"
            ) {
                collections.mobile.bottomCorners = true;
            }
        });
    }

    const backgroundImage = css({
        backgroundImage: totalBGs.map((bg) => bg.src).join(", "),
        backgroundRepeat: totalBGs.map(() => "no-repeat").join(", "),
        backgroundSize: totalBGs.map((bg) => bg.sizeMobile).join(", "),
        backgroundPosition: totalBGs.map((bg) => bg.posMobile).join(", "),

        [media.above.sm]: {
            backgroundSize: totalBGs.map((bg) => bg.sizeTablet).join(", "),
            backgroundPosition: totalBGs.map((bg) => bg.posTablet).join(", ")
        },

        [media.above.lg]: {
            backgroundSize: totalBGs.map((bg) => bg.sizeDesktop).join(", "),
            backgroundPosition: totalBGs.map((bg) => bg.posDesktop).join(", ")
        }
    });

    return (
        <div
            css={[
                obj.bgColor && backgroundColorMap[obj.bgColor],
                !obj.bgColor && css({ backgroundColor: "transparent" }),
                obj.textColor && textColorMap[obj.textColor],
                !obj.textColor && css({ textColor: "#091e42" }),
                (hasGradient || obj.bgImage || obj.partialBackgrounds) &&
                    backgroundImage,
                collections.mobile.topCorners &&
                    css({ paddingBlockStart: "200px" }),
                collections.mobile.bottomCorners &&
                    css({ paddingBlockEnd: "200px" }),
                collections.tablet.topCorners &&
                    css({ [media.above.sm]: { paddingBlockStart: "120px" } }),
                collections.tablet.bottomCorners &&
                    css({ [media.above.sm]: { paddingBlockEnd: "120px" } }),
                collections.desktop.topCorners &&
                    css({ [media.above.lg]: { paddingBlockStart: "120px" } }),
                collections.desktop.bottomCorners &&
                    css({ [media.above.lg]: { paddingBlockEnd: "120px" } })
            ]}
            className="styled-background"
        >
            {obj.children}
        </div>
    );
};
